/**
 * Minimal document.cookie wrapper. Every access is guarded: cookies can be
 * blocked by the browser (privacy settings, non-browser environments), and a
 * throwing cookie accessor must never crash the app.
 */

interface SetCookieOptions {
  maxAgeDays?: number;
}

const DEFAULT_MAX_AGE_DAYS = 30;

const isDocumentAvailable = () => typeof document !== "undefined";

export const getCookie = (name: string): string | undefined => {
  if (!isDocumentAvailable()) return undefined;

  try {
    const encodedName = encodeURIComponent(name);
    const pairs = document.cookie ? document.cookie.split(";") : [];

    for (const pair of pairs) {
      const separatorIndex = pair.indexOf("=");
      if (separatorIndex === -1) continue;

      const pairName = pair.slice(0, separatorIndex).trim();
      if (pairName !== encodedName) continue;

      const rawValue = pair.slice(separatorIndex + 1);
      try {
        return decodeURIComponent(rawValue);
      } catch {
        return rawValue;
      }
    }

    return undefined;
  } catch {
    return undefined;
  }
};

/**
 * Returns whether the cookie is readable back afterwards. A blocked cookie jar
 * accepts the assignment without throwing and simply keeps nothing, so the
 * write has to be verified rather than assumed.
 */
export const setCookie = (
  name: string,
  value: string,
  options: SetCookieOptions = {},
): boolean => {
  if (!isDocumentAvailable()) return false;

  try {
    const maxAgeDays = options.maxAgeDays ?? DEFAULT_MAX_AGE_DAYS;
    const maxAgeSeconds = maxAgeDays * 24 * 60 * 60;
    const isSecureContext = window.location.protocol === "https:";

    const attributes = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      "path=/",
      `max-age=${maxAgeSeconds}`,
      "SameSite=Lax",
      ...(isSecureContext ? ["Secure"] : []),
    ];

    document.cookie = attributes.join("; ");

    return getCookie(name) === value;
  } catch {
    // cookies blocked -- nothing to do
    return false;
  }
};

export const deleteCookie = (name: string): void => {
  if (!isDocumentAvailable()) return;

  try {
    document.cookie = `${encodeURIComponent(name)}=; path=/; max-age=0`;
  } catch {
    // cookies blocked -- nothing to do
  }
};
